import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Icons } from '@ohif/ui-next';
import './Login.css';
import maskGroupImage from '../../../assets/Mask group.png';
import logoWhite from '../../../assets/logo-white.png';
import loadingGif from '../../../assets/loading.gif';

// Healthcare Logo Component
const HealthcareLogo: React.FC = () => {
  return (
    <div className="healthcare-logo">
      <div className="logo-icon">
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
        >
          {/* Heart outline */}
          <path
            d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"
            stroke="currentColor"
            strokeWidth="2"
            fill="none"
          />
          {/* Plus sign inside heart */}
          <path
            d="M12 8v8M8 12h8"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      </div>
      <span className="logo-text">Healthcare</span>
    </div>
  );
};

const Login: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Get the intended destination from location state, or default to '/submissions'
  // Preserve both pathname and search params
  const from = location.state?.from?.pathname || '/submissions';
  const search = location.state?.from?.search || '';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Prevent native browser tooltip ("Please fill in this field") and handle validation ourselves
    if (!username.trim() || !password.trim()) {
      setError('Username and password are required.');
      return;
    }

    setIsLoading(true);

    try {
      const result = await login(username, password);
      console.log('result', result);
      // Only navigate if login was explicitly successful
      if (result && result.success === true) {
        // Always navigate to the intended destination (password change modal will be shown by AuthContext)
        const destination = search ? `${from}${search}` : from;
        console.log('[Login] Navigating to:', destination);
        navigate(destination, { replace: true });
      } else {
        // Show error message and stay on login page
        setError(result?.message || 'Login failed. Please try again.');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-container">
      {/* Loading overlay */}
      {isLoading && (
        <div className="loading-overlay">
          <img src={loadingGif} alt="Loading..." className="loading-spinner" />
        </div>
      )}

      <div className="login-main-content">
        {/* Left side - Image section */}
        <div className="login-image-section">
          <div className="image-container">
            <img
              src={maskGroupImage}
              alt="Chaithanya Eye Hospital"
              className="login-image"
            />
          </div>
        </div>

        {/* Right side - Login form section */}
        <div className="login-form-section">
          <div className="login-form-container">
            <div className="login-header">
              <h1>Welcome</h1>
              <p>Sign in to your account</p>
            </div>

            <form
              onSubmit={handleSubmit}
              className="login-form"
              noValidate
            >
              <div className="form-group margin-bottom-10">
                <label htmlFor="username">Username</label>
                <input
                  type="text"
                  id="username"
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                  placeholder="Please enter your username"
                  required
                  disabled={isLoading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="password">Password</label>
                <div className="password-input-container">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="Enter password"
                    required
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    className="password-toggle-btn"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={isLoading}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? (
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                        <line
                          x1="1"
                          y1="1"
                          x2="23"
                          y2="23"
                        />
                      </svg>
                    ) : (
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle
                          cx="12"
                          cy="12"
                          r="3"
                        />
                      </svg>
                    )}
                  </button>
                </div>
                <div className="forgot-password-container">
                  <label className="remember-me">
                    <input
                      type="checkbox"
                      defaultChecked
                    />
                    <span>Remember Me</span>
                  </label>
                  <button
                    type="button"
                    className="forgot-password-link"
                    onClick={() => {
                      navigate('/forgotpassword');
                    }}
                    disabled={isLoading}
                  >
                    Forgot Password?
                  </button>
                </div>
              </div>

              {error && <div className="error-message">{error}</div>}

              <button
                type="submit"
                className="login-button"
                disabled={isLoading || !username || !password}
              >
                {isLoading ? 'Signing in...' : 'Login'}
              </button>
            </form>

            <div className="login-footer">
              <p className="copyright-text">Copyright © 2025 retinARC. All rights reserved.</p>
            </div>
          </div>
        </div>

        {/* Image overlay positioned over the left section */}
        <div className="image-overlay">
          <div className="hospital-logo">
            <img
              src={logoWhite}
              alt="Chaithanya Eye Hospital & Research Institute"
              className="logo-image"
            />
          </div>
          <div className="hospital-tagline">
            <div className="tagline-line-1">Vision to Empower Eye Care</div>
            <div className="tagline-line-2">Across All Walks of Life</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
